/**
 * SIDEBAR - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  BookOpenIcon,
  FolderIcon,
  FlagIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
  InboxIcon,
  HeartIcon,
  BanknotesIcon,
  PhoneIcon,
  XMarkIcon,
  CalendarDaysIcon,
  BookmarkIcon,
  ClockIcon,
  UserGroupIcon,
  ShareIcon,
  GiftIcon,
  SparklesIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  BugAntIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { COLORS, SPACING, RADIUS, TYPOGRAPHY } from '../../design/tokens';
import { useIsDesktop } from '../../hooks/useMediaQuery';

interface SidebarProps {
  onClose?: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
}

interface NavCategory {
  name: string;
  items: NavItem[];
}

const navigationCategories: NavCategory[] = [
  {
    name: 'Productivity',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
      { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon },
      { name: 'Inbox', href: '/emails', icon: InboxIcon },
      { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon },
      { name: 'Expectations', href: '/expectations', icon: ClockIcon },
      { name: 'Goals', href: '/goals', icon: FlagIcon },
    ],
  },
  {
    name: 'Knowledge',
    items: [
      { name: 'Journal', href: '/journal', icon: BookOpenIcon },
      { name: 'Learning', href: '/learning', icon: BookmarkIcon },
      { name: 'Documents', href: '/documents', icon: DocumentIcon },
      { name: 'Glossary', href: '/glossary', icon: AcademicCapIcon },
    ],
  },
  {
    name: 'Work',
    items: [
      { name: 'Projects', href: '/projects', icon: FolderIcon },
      { name: 'Businesses', href: '/businesses', icon: BriefcaseIcon },
      { name: 'Sprint History', href: '/sprint-history', icon: ChartBarIcon },
      { name: 'Meetings', href: '/meetings', icon: PhoneIcon },
    ],
  },
  {
    name: 'People',
    items: [
      { name: 'People', href: '/people', icon: UsersIcon },
      { name: 'Networking', href: '/networking', icon: ShareIcon },
      { name: 'Mentoring', href: '/mentoring', icon: UserGroupIcon },
    ],
  },
  {
    name: 'Life',
    items: [
      { name: 'Habits', href: '/habits', icon: ClockIcon },
      { name: 'Health - Fitness', href: '/health', icon: HeartIcon },
      { name: 'Finance', href: '/finance', icon: BanknotesIcon },
      { name: 'Home', href: '/home', icon: BuildingOfficeIcon },
      { name: 'Dreams', href: '/dreams', icon: SparklesIcon },
      { name: 'Wish List', href: '/wishlist', icon: GiftIcon },
    ],
  },
  {
    name: 'Tools',
    items: [
      { name: 'Tools', href: '/tools', icon: WrenchScrewdriverIcon },
      { name: 'Tracking', href: '/tracking', icon: BugAntIcon },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const isDesktop = useIsDesktop();
  // All categories expanded by default
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(navigationCategories.map((cat) => cat.name))
  );

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '256px',
        backgroundColor: COLORS.bg.panel,
        borderRight: `1px solid ${COLORS.border.default}`,
      }}
    >
      {/* Logo */}
      <div
        style={{
          position: 'relative',
          height: '112px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${COLORS.border.default}`,
          padding: SPACING[16],
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING[4] }}>
          <img src="/os.png" alt="FloOS Logo" style={{ height: '80px' }} />
          <span style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.tertiary,
          }}>
            v{process.env.REACT_APP_BUILD_TIME || 'dev'}
          </span>
        </div>
        {onClose && !isDesktop && (
          <button
            style={{
              position: 'absolute',
              top: SPACING[16],
              right: SPACING[16],
              padding: SPACING[8],
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: RADIUS,
              cursor: 'pointer',
              transition: 'background-color 0.1s ease',
            }}
            onClick={onClose}
            aria-label="Close sidebar"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <XMarkIcon style={{ width: '24px', height: '24px', color: COLORS.text.secondary }} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: SPACING[12],
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING[8],
        }}
      >
        {navigationCategories.map((category) => {
          const isExpanded = expandedCategories.has(category.name);

          return (
            <div key={category.name}>
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: COLORS.text.tertiary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: RADIUS,
                  cursor: 'pointer',
                  transition: 'all 0.1s ease',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = COLORS.bg.hover;
                  e.currentTarget.style.color = COLORS.text.secondary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.text.tertiary;
                }}
              >
                <span>{category.name}</span>
                {isExpanded ? (
                  <ChevronDownIcon style={{ width: '16px', height: '16px' }} />
                ) : (
                  <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
                )}
              </button>

              {/* Category Items */}
              {isExpanded && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: SPACING[4],
                    marginTop: SPACING[4],
                  }}
                >
                  {category.items.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      end={item.href === '/'}
                      style={({ isActive }) => ({
                        display: 'flex',
                        alignItems: 'center',
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        fontWeight: '500',
                        borderRadius: RADIUS,
                        textDecoration: 'none',
                        transition: 'all 0.1s ease',
                        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        color: isActive ? COLORS.accent.default : COLORS.text.secondary,
                      })}
                      onClick={onClose ? () => onClose() : undefined}
                      onMouseEnter={(e) => {
                        const target = e.currentTarget as HTMLElement;
                        if (!target.classList.contains('active')) {
                          target.style.backgroundColor = COLORS.bg.hover;
                          target.style.color = COLORS.text.primary;
                        }
                      }}
                      onMouseLeave={(e) => {
                        const target = e.currentTarget as HTMLElement;
                        if (!target.classList.contains('active')) {
                          target.style.backgroundColor = 'transparent';
                          target.style.color = COLORS.text.secondary;
                        }
                      }}
                    >
                      <item.icon style={{ width: '20px', height: '20px', marginRight: SPACING[12], flexShrink: 0 }} />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
